import { createFileRoute, Link } from "@tanstack/react-router";
import {
  ArrowRight,
  PawPrint,
  GraduationCap,
  Stethoscope,
  UtensilsCrossed,
  Sparkles,
  Leaf,
  LifeBuoy,
  ShieldCheck,
  Quote,
  Users,
  HandHeart,
  ChevronRight,
  Flower2,
  Baby,
  Palette,
  HeartHandshake,
  AlertTriangle,
  Building2,
  Star,
  CheckCircle2,
  PhoneCall,
  Heart,
  Clock,
  Award,
  Shield,
  FileText,
  Search,
} from "lucide-react";
import { SiteShell } from "@/components/site-shell";
import {
  initiatives,
  foundationStats,
  values,
  pressMentions,
  supportCategories,
  manavtaLogo,
} from "@/data/sample";

const logoUrl = manavtaLogo;

const ICONS: Record<string, typeof PawPrint> = {
  PawPrint,
  GraduationCap,
  Stethoscope,
  UtensilsCrossed,
  Sparkles,
  Leaf,
  LifeBuoy,
  Users,
  Flower2,
  Baby,
  Palette,
  HeartHandshake,
};

export const Route = createFileRoute("/")({
  head: () => ({
    meta: [
      { title: "Manavta — Give with Heart. Give with Confidence." },
      {
        name: "description",
        content:
          "Manavta brings together associations, communities, sanghs, clubs, animal shelters and non-profits with people who want to help. 80G & 12A certified.",
      },
      { property: "og:title", content: "Manavta" },
      {
        property: "og:description",
        content:
          "Give with Heart. Give with Confidence. India's transparent giving platform — 80G & 12A certified.",
      },
      { property: "og:type", content: "website" },
    ],
  }),
  component: Home,
});

function Home() {
  return (
    <SiteShell>
      <HeroSection />
      <CategoryOrbitSection />
      <TrustBar />
      <InitiativesSection />
      <FeaturedPanjrapoleSection />
      <PressMentionsSection />
    </SiteShell>
  );
}

{
  /* ── 1. Hero Section (Light Theme - Professional & Elegant) ───────────── */
}
function HeroSection() {
  return (
    <section
      className="relative overflow-hidden py-14 sm:py-20 lg:py-24"
      style={{
        background: "linear-gradient(135deg, #fffbf5 0%, #fff8ee 35%, #fef9f0 60%, #f8f4ff 100%)",
      }}
    >
      {/* Decorative Background Orbs */}
      <div
        className="absolute top-[-80px] right-[-80px] -z-10 h-[520px] w-[520px] rounded-full opacity-30"
        style={{ background: "radial-gradient(circle, #f59e0b 0%, #fbbf24 40%, transparent 70%)" }}
      />
      <div
        className="absolute bottom-[-120px] left-[-100px] -z-10 h-[480px] w-[480px] rounded-full opacity-20"
        style={{ background: "radial-gradient(circle, #6366f1 0%, #818cf8 50%, transparent 70%)" }}
      />
      <div
        className="absolute top-[40%] left-[45%] -z-10 h-[300px] w-[300px] rounded-full opacity-15"
        style={{ background: "radial-gradient(circle, #10b981 0%, #34d399 50%, transparent 70%)" }}
      />

      {/* Subtle dot-grid texture */}
      <div
        className="absolute inset-0 -z-10 opacity-[0.035]"
        style={{
          backgroundImage: "radial-gradient(circle, #4338ca 1px, transparent 1px)",
          backgroundSize: "28px 28px",
        }}
      />

      <div className="mx-auto max-w-7xl px-4 sm:px-6">
        <div className="grid gap-12 items-center">
          {/* Left Text Content */}
          <div className="flex flex-col max-w-3xl">
            {/* Top Certification Badge */}
            <div
              className="inline-flex items-center gap-2.5 rounded-full px-4 py-2 text-xs font-bold w-fit mb-7 shadow-sm border"
              style={{
                background: "linear-gradient(135deg, #fff7ed, #fffbf5)",
                borderColor: "#f59e0b40",
                color: "#92400e",
              }}
            >
              <img src={logoUrl} alt="Manavta" className="h-4 w-auto object-contain" />
              <span className="h-1.5 w-1.5 rounded-full" style={{ background: "#f59e0b" }} />
              <span className="flex items-center gap-1 font-semibold" style={{ color: "#15803d" }}>
                <CheckCircle2 className="h-3.5 w-3.5" />
                80G Certified
              </span>
            </div>

            {/* Main Heading */}
            <h1
              className="font-display text-4xl font-extrabold leading-[1.07] tracking-tight sm:text-5xl lg:text-[58px]"
              style={{ color: "#1e1b4b" }}
            >
              Any person of any age group can donate any amount, anywhere, for Manavta
            </h1>

            {/* Subheading */}
            <p
              className="mt-6 max-w-[540px] text-base leading-relaxed sm:text-lg"
              style={{ color: "#4b5563" }}
            >
              It's more than a donation — it's{" "}
              <strong style={{ color: "#374151" }}>hope, love and a future transformed.</strong>{" "}
              Manavta brings together associations, communities, sanghs, clubs, animal shelters and
              non-profits with people who want to help. Every rupee you give touches a real life.
            </p>

            {/* Action Buttons */}
            <div className="mt-9 flex flex-wrap items-center gap-4">
              <Link
                to="/donate"
                className="inline-flex items-center gap-2.5 rounded-full px-8 py-3.5 text-sm font-extrabold text-white shadow-lg transition-all transform hover:-translate-y-1 hover:shadow-xl active:scale-95 cursor-pointer"
                style={{
                  background: "linear-gradient(135deg, #f59e0b 0%, #d97706 100%)",
                  boxShadow: "0 8px 25px -6px rgba(245,158,11,0.5)",
                }}
              >
                <HandHeart className="h-4.5 w-4.5" />
                Donate Now
              </Link>
              <Link
                to="/register-institution"
                className="inline-flex items-center gap-2.5 rounded-full px-6 py-3.5 text-sm font-bold transition-all hover:shadow-md active:scale-95 cursor-pointer border-2"
                style={{
                  borderColor: "#e2e8f0",
                  color: "#374151",
                  background: "rgba(255,255,255,0.8)",
                  backdropFilter: "blur(8px)",
                }}
              >
                <Building2 className="h-4.5 w-4.5" style={{ color: "#d97706" }} />
                Register Institute
              </Link>
            </div>

            {/* Star Rating Strip */}
            <div className="mt-7 flex items-center gap-3">
              <div className="flex items-center gap-0.5" style={{ color: "#f59e0b" }}>
                {[...Array(5)].map((_, i) => (
                  <Star key={i} className="h-4 w-4 fill-current" />
                ))}
              </div>
              <p className="text-xs" style={{ color: "#6b7280" }}>
                <span className="font-bold" style={{ color: "#1e1b4b" }}>
                  4.9/5
                </span>{" "}
                · Trusted by{" "}
                <span className="font-bold" style={{ color: "#1e1b4b" }}>
                  1,850+
                </span>{" "}
                active volunteers & donors across Gujarat & India
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

{
  /* ── 1b. Category Orbit: logo surrounded by animated cause icons ────────── */
}
const CATEGORY_ORBIT = [
  { label: "Gaushala", Icon: PawPrint, gradient: "linear-gradient(135deg, #fbbf24, #f59e0b)" },
  {
    label: "Vidhva Ashram",
    Icon: Heart,
    gradient: "linear-gradient(135deg, #fb7185, #e11d48)",
  },
  {
    label: "Vrudha Ashram",
    Icon: HeartHandshake,
    gradient: "linear-gradient(135deg, #c084fc, #a855f7)",
  },
  {
    label: "Mentally Retarded Children Home",
    Icon: Baby,
    gradient: "linear-gradient(135deg, #38bdf8, #0ea5e9)",
  },
  {
    label: "Health Care",
    Icon: Stethoscope,
    gradient: "linear-gradient(135deg, #34d399, #10b981)",
  },
  { label: "Remand Home", Icon: Shield, gradient: "linear-gradient(135deg, #818cf8, #6366f1)" },
  {
    label: "Animal Hospital",
    Icon: PawPrint,
    gradient: "linear-gradient(135deg, #fbbf24, #f59e0b)",
  },
  { label: "Aandhjan Mandal", Icon: Sparkles, gradient: "linear-gradient(135deg, #fb7185, #e11d48)" },
  {
    label: "Apang Manav Mandal",
    Icon: Users,
    gradient: "linear-gradient(135deg, #c084fc, #a855f7)",
  },
  {
    label: "Education",
    Icon: GraduationCap,
    gradient: "linear-gradient(135deg, #38bdf8, #0ea5e9)",
  },
  { label: "Others", Icon: Sparkles, gradient: "linear-gradient(135deg, #34d399, #10b981)" },
];

function CategoryOrbitLogo({
  className,
  ringInset,
  imgClassName,
}: {
  className: string;
  ringInset: string;
  imgClassName: string;
}) {
  return (
    <div
      className={`relative flex items-center justify-center rounded-full bg-white shadow-2xl border border-amber-100 ${className}`}
    >
      <div
        className={`absolute rounded-full border-2 border-dashed opacity-40 animate-spin ${ringInset}`}
        style={{ borderColor: "#f59e0b", animationDuration: "16s" }}
      />
      <div
        className="absolute inset-2 rounded-full animate-pulse opacity-20"
        style={{ background: "radial-gradient(circle, #f59e0b, transparent 70%)" }}
      />
      <img src={logoUrl} alt="Manavta" className={`relative object-contain ${imgClassName}`} />
    </div>
  );
}

function CategoryOrbitSection() {
  const radius = 300;
  return (
    <section className="relative overflow-hidden bg-white py-16 sm:py-20 lg:py-24">
      <div
        className="absolute top-1/2 left-1/2 -z-10 h-[560px] w-[560px] -translate-x-1/2 -translate-y-1/2 rounded-full opacity-[0.07]"
        style={{ background: "radial-gradient(circle, #6366f1, transparent 70%)" }}
      />

      <div className="mx-auto max-w-7xl px-4 sm:px-6 text-center">
        <p
          className="text-xs font-extrabold uppercase tracking-widest mb-2"
          style={{ color: "#d97706" }}
        >
          Where Your Support Goes
        </p>
        <h2
          className="font-display text-2xl sm:text-3xl font-extrabold mb-12"
          style={{ color: "#1e1b4b" }}
        >
          One Manavta, Many Causes
        </h2>

        {/* Desktop / Large screens: logo with orbiting category boxes */}
        <div className="relative mx-auto hidden lg:block" style={{ width: 720, height: 720 }}>
          <div className="absolute top-1/2 left-1/2 z-10 -translate-x-1/2 -translate-y-1/2">
            <CategoryOrbitLogo
              className="h-56 w-56"
              ringInset="inset-[-14px]"
              imgClassName="h-28 w-28"
            />
          </div>

          {CATEGORY_ORBIT.map((cat, i) => {
            const angle = (360 / CATEGORY_ORBIT.length) * i - 90;
            return (
              <div
                key={cat.label}
                className="absolute top-1/2 left-1/2"
                style={{
                  transform: `translate(-50%, -50%) rotate(${angle}deg) translateX(${radius}px) rotate(${-angle}deg)`,
                }}
              >
                <div
                  className="group flex flex-col items-center gap-2 animate-float"
                  style={{
                    animationDelay: `${i * 0.18}s`,
                    animationDuration: `${3 + (i % 3) * 0.4}s`,
                  }}
                >
                  <div
                    className="flex h-16 w-16 items-center justify-center rounded-2xl shadow-lg transition-all duration-300 group-hover:-translate-y-2 group-hover:scale-110 group-hover:shadow-xl"
                    style={{ background: cat.gradient }}
                  >
                    <cat.Icon className="h-7 w-7 text-white" />
                  </div>
                  <p
                    className="w-24 text-[11px] font-bold leading-tight"
                    style={{ color: "#1e1b4b" }}
                  >
                    {cat.label}
                  </p>
                </div>
              </div>
            );
          })}
        </div>

        {/* Tablet / Mobile: logo above a wrapping grid of category boxes */}
        <div className="lg:hidden">
          <div className="mb-10 flex justify-center">
            <CategoryOrbitLogo
              className="h-40 w-40 sm:h-48 sm:w-48"
              ringInset="inset-[-10px]"
              imgClassName="h-20 w-20 sm:h-24 sm:w-24"
            />
          </div>
          <div className="grid grid-cols-3 gap-4 sm:grid-cols-4">
            {CATEGORY_ORBIT.map((cat, i) => (
              <div
                key={cat.label}
                className="flex flex-col items-center gap-2 animate-float"
                style={{
                  animationDelay: `${i * 0.15}s`,
                  animationDuration: `${3 + (i % 3) * 0.4}s`,
                }}
              >
                <div
                  className="flex h-14 w-14 items-center justify-center rounded-2xl shadow-lg transition-transform duration-300 active:scale-95 sm:h-16 sm:w-16"
                  style={{ background: cat.gradient }}
                >
                  <cat.Icon className="h-6 w-6 text-white sm:h-7 sm:w-7" />
                </div>
                <p className="text-[10px] font-bold leading-tight" style={{ color: "#1e1b4b" }}>
                  {cat.label}
                </p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

{
  /* ── 2. Light Theme Trust Bar ───────────────────────────────────────────── */
}
function TrustBar() {
  const badges = [
    { label: "80G & 12A Certified", sub: "Tax-exempt receipts", Icon: ShieldCheck },
    { label: "Indian Citizens & Residents", sub: "Donations accepted", Icon: Users },
    { label: "Instant Digital Receipt", sub: "80G receipt by email", Icon: FileText },
    { label: "Verified Every Applicant", sub: "Multi-level audit", Icon: Shield },
    { label: "Trackable Fund Usage", sub: "Real-time updates", Icon: Clock },
    { label: "Annual Reports", sub: "Full transparency", Icon: Award },
  ];
  return (
    <section className="bg-white border-y border-amber-900/10 py-4">
      <div className="mx-auto max-w-7xl px-4 sm:px-6">
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
          {badges.map((b) => (
            <div key={b.label} className="flex items-center gap-3 p-2">
              <b.Icon className="h-5 w-5 text-[var(--saffron)] shrink-0" />
              <div>
                <p className="font-bold text-xs text-slate-900 leading-tight">{b.label}</p>
                <p className="text-[10px] text-slate-500 mt-0.5">{b.sub}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

{
  /* ── 4. White Section: Our Initiatives Grid ───────────────────────────── */
}
function InitiativesSection() {
  return (
    <section className="bg-white py-16 sm:py-20">
      <div className="mx-auto max-w-7xl px-4 sm:px-6">
        {/* Header */}
        <div className="mb-10 flex items-end justify-between gap-4">
          <div>
            <p className="text-xs font-extrabold uppercase tracking-[0.2em] text-[var(--saffron-deep)]">
              OUR INITIATIVES
            </p>
          </div>
          <Link
            to="/initiatives"
            className="hidden sm:inline-flex shrink-0 items-center gap-1 text-sm font-bold text-[var(--maroon)] hover:gap-2 transition-all"
          >
            View all <ChevronRight className="h-4 w-4" />
          </Link>
        </div>

        {/* Row 1: 3 Premium Horizontal Split Cards */}
        <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {initiatives.slice(0, 3).map((i) => {
            const Icon = ICONS[i.icon] ?? PawPrint;
            return (
              <Link
                key={i.slug}
                to="/initiatives/$slug"
                params={{ slug: i.slug }}
                className="group flex flex-col rounded-2xl overflow-hidden border hover:shadow-xl hover:-translate-y-1 transition-all duration-300 cursor-pointer bg-white"
                style={{ borderColor: "#eee8f0" }}
              >
                {/* Image — top 55% */}
                <div className="relative h-[190px] overflow-hidden">
                  <img
                    src={i.cover}
                    alt={i.name}
                    className="absolute inset-0 h-full w-full object-cover group-hover:scale-105 transition-transform duration-700"
                  />
                  {/* Very light bottom fade */}
                  <div
                    className="absolute inset-0"
                    style={{
                      background:
                        "linear-gradient(to top, rgba(255,255,255,0.4) 0%, transparent 50%)",
                    }}
                  />
                  {/* Icon badge top-left */}
                  <div className="absolute top-3 left-3 z-10">
                    <div
                      className="h-9 w-9 rounded-xl flex items-center justify-center shadow-lg border border-white/40 backdrop-blur-sm"
                      style={{ background: i.color }}
                    >
                      <Icon className="h-4.5 w-4.5 text-white" />
                    </div>
                  </div>
                  {/* Coming Soon badge */}
                  {!i.live && (
                    <div className="absolute top-3 right-3 z-10">
                      <span
                        className="rounded-full px-2.5 py-1 text-[9px] font-extrabold uppercase tracking-wider shadow-sm"
                        style={{ background: "#f59e0b", color: "#1c1917" }}
                      >
                        Coming Soon
                      </span>
                    </div>
                  )}
                  {/* Colored accent bottom strip */}
                  <div
                    className="absolute bottom-0 left-0 right-0 h-[3px]"
                    style={{ background: i.color }}
                  />
                </div>

                {/* White Content Panel */}
                <div className="flex flex-col flex-1 p-4">
                  <p
                    className="text-[10px] font-extrabold uppercase tracking-widest mb-1"
                    style={{ color: i.color }}
                  >
                    {i.tagline}
                  </p>
                  <p
                    className="font-display text-lg font-extrabold leading-tight"
                    style={{ color: "#1e1b4b" }}
                  >
                    {i.name}
                  </p>
                  <p className="text-xs text-gray-500 mt-2 leading-relaxed line-clamp-2">
                    {i.short}
                  </p>
                  <div
                    className="mt-3 flex items-center border-t pt-3"
                    style={{ borderColor: "#f5f0eb" }}
                  >
                    <span
                      className="ml-auto inline-flex items-center gap-1 text-[10px] font-bold group-hover:gap-2 transition-all shrink-0"
                      style={{ color: i.color }}
                    >
                      Explore <ArrowRight className="h-3 w-3" />
                    </span>
                  </div>
                </div>
              </Link>
            );
          })}
        </div>

        {/* Row 2: 4 Smaller Split Cards */}
        <div className="grid gap-4 mt-5 grid-cols-2 lg:grid-cols-4">
          {initiatives.slice(3).map((i) => {
            const Icon = ICONS[i.icon] ?? PawPrint;
            return (
              <Link
                key={i.slug}
                to="/initiatives/$slug"
                params={{ slug: i.slug }}
                className="group flex flex-col rounded-xl overflow-hidden border hover:shadow-lg hover:-translate-y-1 transition-all duration-300 cursor-pointer bg-white"
                style={{ borderColor: "#eee8f0" }}
              >
                {/* Image top */}
                <div className="relative h-[110px] overflow-hidden">
                  <img
                    src={i.cover}
                    alt={i.name}
                    className="absolute inset-0 h-full w-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                  <div
                    className="absolute inset-0"
                    style={{
                      background:
                        "linear-gradient(to top, rgba(255,255,255,0.35) 0%, transparent 55%)",
                    }}
                  />
                  <div className="absolute top-2 left-2 z-10">
                    <div
                      className="h-6 w-6 rounded-md flex items-center justify-center shadow-md"
                      style={{ background: i.color }}
                    >
                      <Icon className="h-3 w-3 text-white" />
                    </div>
                  </div>
                  <div
                    className="absolute bottom-0 left-0 right-0 h-[2.5px]"
                    style={{ background: i.color }}
                  />
                </div>
                {/* White content */}
                <div className="p-2.5 flex flex-col flex-1">
                  <p
                    className="font-display font-extrabold text-xs leading-tight"
                    style={{ color: "#1e1b4b" }}
                  >
                    {i.name}
                  </p>
                  <p
                    className="text-[9px] mt-0.5 line-clamp-2 leading-snug"
                    style={{ color: "#6b7280" }}
                  >
                    {i.short}
                  </p>
                  <div
                    className="mt-auto pt-2 inline-flex items-center gap-1 text-[9px] font-bold group-hover:gap-2 transition-all"
                    style={{ color: i.color }}
                  >
                    Explore <ArrowRight className="h-2.5 w-2.5" />
                  </div>
                </div>
              </Link>
            );
          })}
        </div>
      </div>
    </section>
  );
}

{
  /* ── 5. Platform Showcase: All Seva Initiatives ─────────────────────────── */
}
function FeaturedPanjrapoleSection() {
  return (
    <section className="mx-auto max-w-7xl px-4 py-10 sm:px-6">
      <div
        className="overflow-hidden rounded-3xl shadow-2xl"
        style={{ background: "linear-gradient(135deg, #0c1226 0%, #0f172a 60%, #111827 100%)" }}
      >
        {/* Top Header Bar */}
        <div className="px-8 py-10 sm:px-12">
          <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4">
            <div>
              <span
                className="inline-flex items-center gap-1.5 rounded-full border px-3.5 py-1 text-[11px] font-extrabold uppercase tracking-wider mb-3"
                style={{
                  borderColor: "#f59e0b50",
                  color: "#f59e0b",
                  background: "rgba(245,158,11,0.1)",
                }}
              >
                <Heart className="h-3 w-3 fill-current" /> MANAVTA PLATFORM
              </span>
              <h2 className="font-display text-2xl font-extrabold sm:text-3xl leading-tight text-white">
                One platform. Every cause. Total transparency.
              </h2>
              <p className="mt-2 text-slate-400 text-sm max-w-xl leading-relaxed">
                Donate to verified Panjrapoles, schools, hospitals, kitchens, women's groups and
                disaster relief teams — all under one 80G-certified platform with real-time impact
                proof.
              </p>
            </div>
            <Link
              to="/causes"
              className="shrink-0 inline-flex items-center gap-2 rounded-full px-6 py-3 text-xs font-extrabold transition-all hover:shadow-lg hover:-translate-y-0.5"
              style={{ background: "linear-gradient(135deg, #f59e0b, #d97706)", color: "#1c1917" }}
            >
              Browse All Causes <ArrowRight className="h-3.5 w-3.5" />
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
}

{
  /* ── 6. Warm Cream Section: Press Mentions ────────────────────────────── */
}
function PressMentionsSection() {
  return (
    <section className="bg-[#fefaf3] py-16 sm:py-24 border-y border-amber-900/10">
      <div className="mx-auto max-w-7xl px-4 sm:px-6">
        {/* Press Mentions Marquee */}
        <div className="overflow-hidden w-full relative">
          {/* Subtle fade edges for the marquee */}
          <div className="absolute inset-y-0 left-0 w-16 sm:w-32 bg-gradient-to-r from-[#fefaf3] to-transparent z-10 pointer-events-none" />
          <div className="absolute inset-y-0 right-0 w-16 sm:w-32 bg-gradient-to-l from-[#fefaf3] to-transparent z-10 pointer-events-none" />

          <div className="flex w-max animate-marquee gap-6 py-4 hover:[animation-play-state:paused]">
            {[...pressMentions, ...pressMentions].map((p, idx) => (
              <div
                key={`${p.quote}-${idx}`}
                className="w-[320px] shrink-0 rounded-2xl bg-white p-6 border border-amber-900/10 shadow-sm flex flex-col justify-center items-center text-center transition hover:shadow-md"
              >
                <Quote className="h-6 w-6 text-[var(--saffron)] mb-4 opacity-80" />
                <p className="text-[13px] font-semibold leading-relaxed text-[var(--maroon-deep)] italic">
                  "{p.quote}"
                </p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
